import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AuthService } from '../../Auth/auth.service';
import { format, toZonedTime } from 'date-fns-tz';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
  
})
export class DataService {

  constructor(private http:HttpClient,  private authService:AuthService){}
  private baseUrl = environment.apiBaseUrl;
  private Faq = `${environment.apiBaseUrl}/faq`;
  private pageUrl = `${environment.apiBaseUrl}/page`;
  private auditUrl = `${environment.apiBaseUrl}/audit`;


  private currentUserSubject = new BehaviorSubject<any | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  private companySubject = new BehaviorSubject<any | null>(null);
  company$ = this.companySubject.asObservable();
  
  private getAuthHeaders(): HttpHeaders {
    const accessToken = this.authService.getAccessToken();  
    if (!accessToken) {
        throw new Error('No access token found');
    }
    return new HttpHeaders({
        'Authorization': `Bearer ${accessToken.trim()}`,
    });
  }


  
  //for consistent local date across app 
  getLocalDate(): string {
    const timeZone = 'America/Port_of_Spain'; // or use Intl.DateTimeFormat().resolvedOptions().timeZone
    const now = new Date();
    const localNow = toZonedTime(now, timeZone);

    const formatted = format(localNow, 'yyyy-MM-dd', { timeZone });
    return formatted;
  }


  setCurrentUser(user: any | null) {
    this.currentUserSubject.next(user);
  }

  getCurrentUser(forceRefresh = false): Observable<any> {
    console.log('[getCurrentUser] Called');

    if (!forceRefresh && this.currentUserSubject.value) {
      return of(this.currentUserSubject.value); // cached user
    }

    const accessToken = this.authService.getAccessToken();  
    if (!accessToken) {
        console.error('[getCurrentUser] No access token found');
        return throwError(() => new Error('No access token found'));
    }

    console.log('[getCurrentUser] Using access token:', accessToken);

    const headers = this.getAuthHeaders();
    const userEmail = localStorage.getItem('userEmail');
    // console.log('[getCurrentUser] Looking for user with email:', userEmail);

    return this.http.get<any[]>(`${this.baseUrl}/user`, { headers }).pipe(
        map(users => {
            const userEmail = localStorage.getItem('userEmail');
            const foundUser = users.find(user => user.strEmail?.toLowerCase() === userEmail?.toLowerCase());
            if (!foundUser) {
              console.warn(`[getCurrentUser] No match for email "${userEmail}"`);
            } else {
            console.log('[getCurrentUser] Matched user:', foundUser);
              this.currentUserSubject.next(foundUser); 
            }
            return foundUser;
        }),
        catchError((error) => {
            console.error('[getCurrentUser] Error fetching users:', error);
            if (error.status === 403) { 
                console.error('[getCurrentUser] Access denied - Token expired or invalid');
                return this.authService.handleTokenExpiration().pipe(
                    switchMap(() => {
                        const newToken = this.authService.getAccessToken();
                        console.log('[getCurrentUser] New access token:', newToken);
                        const newHeaders = new HttpHeaders({
                            'Authorization': `Bearer ${newToken}`,
                        });

                        return this.http.get<any[]>(`${this.baseUrl}/user`, { headers: newHeaders }).pipe(
                            tap(users => console.log('[getCurrentUser] Retrying fetch users:', users)), 
                            map(users => {
                                const refreshedUser = users.find(user => user.email === userEmail);
                                // console.log('[getCurrentUser] Matched user after token refresh:', refreshedUser);
                                if (refreshedUser) {
                                  this.currentUserSubject.next(refreshedUser);
                                }
                                return refreshedUser;
                            })
                        );
                    }),
                    catchError((refreshError) => {
                        console.error('[getCurrentUser] Token refresh failed:', refreshError);
                        return throwError(() => new Error('Failed to refresh token'));
                    })
                );
            }
            return throwError(() => error);  
        })
      );
  }

  
  getUserByPK(pk: number): Observable<any>{
    const accessToken = this.authService.getAccessToken();  
    if (!accessToken) {
        console.error('[getUserByPK] No access token found');
        return throwError(() => new Error('No access token found'));
    }

    const headers = this.getAuthHeaders();
    return this.http.get<any[]>(`${this.baseUrl}/user`, { headers }).pipe(
        map(users => {
            const foundUser = users.find(user => user.intUserPK === pk);
            // console.log('[getUserByPK] Matched user:', foundUser);
            return foundUser;
        }),
        catchError((error) => {
            console.error('[getUserByPK] Error fetching users:', error);
            return throwError(() => error);
        })
    );
  }

  getAllUsers(): Observable<any[]> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
  
        return this.http.get<any[]>(`${this.baseUrl}/user`, { headers });
      }),
      catchError((error) => {
        console.error('Failed to fetch users', error);
        return throwError(() => error);
      })
    );
  }
  // getUserEmailsByCompany(): Observable<string> {
  //   return this.getAllUsers().pipe(
  //     map((users: any[]) => {
  //       return users
  //         .filter(user => user.intCompanyFK === 1) // RETURNS ALL UNIPET EMAILS (PRC STAFF)
  //         .map(user => user.strEmail)
  //         .join(',');
  //     }),
  //     catchError(error => {
  //       console.error('Failed to get user emails', error);
  //       return throwError(() => error);
  //     })
  //   );
  // }  
  
  getCompanyByPK(pk: number): Observable<any> {
    // console.log(`[getCompanyByPK] Fetching company with PK: ${pk}`);

    // Return cached company if it exists
    if (this.companySubject.value && this.companySubject.value.intCompanyPK === pk) {
        // console.log('[getCompanyByPK] Returning cached company');
        return of(this.companySubject.value);
    }

    const accessToken = this.authService.getAccessToken();  
    if (!accessToken) {
        console.error('[getCompanyByPK] No access token found');
        return throwError(() => new Error('No access token found'));
    }

    const headers = this.getAuthHeaders();
    return this.http.get<any[]>(`${this.baseUrl}/company`, { headers }).pipe(
        map(companies => {
            const foundCompany = companies.find(company => company.intCompanyPK === pk);
            // console.log('[getCompanyByPK] Matched company:', foundCompany);

            // Update BehaviorSubject
            if (foundCompany) {
                this.companySubject.next(foundCompany);
            }

            return foundCompany;
        }),
        catchError((error) => {
            console.error('[getCompanyByPK] Error fetching companies:', error);
            return throwError(() => error);
        })
    );
  }

  //200
  getCompanyBusinessTypes(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        console.log('Using Token:', `${accessToken}`);
        const headers = this.getAuthHeaders();
        return this.http.get<any[]>(`${this.baseUrl}/companyBusinessType?companyId=${companyId}`, { headers }).pipe(
          // tap(response => console.log('Business Types Response:', response))
        );
      }),
      catchError((error) => {
        console.error('Failed to fetch company business types:', error);
        return throwError(() => error);
      })
    );
  }//will be an int that needs to be mapped to company types.
  
  //200
  getCompanyContracts(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
        switchMap(() => {
            const headers = this.getAuthHeaders(); 
            return this.http.get<any[]>(`${this.baseUrl}/companyContract?companyId=${companyId}`, { headers }).pipe(
              // tap(response => console.log('Contracts Response:', response))
            );
          }),
          catchError((error) => {
            console.error('Failed to fetch company contracts:', error);
            return throwError(() => error);
          })
        );
      }

  //200
  getCompanyContractDetails(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        return this.http.get<any[]>(`${this.baseUrl}/companyContractDetails?companyId=${companyId}`, { headers }).pipe(
          // tap(response => console.log('Contract Details Response:', response))
        );
      }),
      catchError((error) => {
        console.error('Failed to fetch CompanyContractDetails:', error);
        return throwError(() => error);
      })
    );
  }
  
  //200
  getCompanyDirectors(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        return this.http.get<any[]>(`${this.baseUrl}/companydirector?companyId=${companyId}`, { headers }).pipe(
          // tap(response => console.log('Directors Response:', response))
        );
      }),
      catchError((error) => {
        console.error('Failed to fetch CompanyDirectors:', error);
        return throwError(() => error);
      })
    );
  }

  //200
  getCompanyDocuments(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        return this.http.get<any[]>(`${this.baseUrl}/companydocument?companyId=${companyId}`, { headers }).pipe(
          // tap(response => console.log('Documents Response:', response))
        );
      }),
      catchError((error) => {
        console.error('Failed to fetch CompanyDocuments:', error);
        return throwError(() => error);
      })
    );
  }

  getVendorConsent(companyId: number) {
    type VendorConsentRecord = {
      accepted: boolean;
      consentVersion: string;
      consentText: string;
      acceptedAt: string;
    };

    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        return this.fetchVendorConsentFromUrl(
          `${this.baseUrl}/user/consent/${companyId}`,
          headers
        ).pipe(
          catchError((primaryError) => {
            if (primaryError?.status === 404 || primaryError?.status === 405 || primaryError?.status === 500) {
              return this.fetchVendorConsentFromUrl(
                `${this.baseUrl}/vendorconsent/company/${companyId}`,
                headers
              ).pipe(
                catchError((fallbackError) => {
                  if (fallbackError?.status === 404) {
                    return of({ record: null as VendorConsentRecord | null, absent: true });
                  }
                  console.error('Failed to fetch vendor consent:', fallbackError);
                  return of({ record: null, absent: false });
                })
              );
            }
            console.error('Failed to fetch vendor consent:', primaryError);
            return of({ record: null as VendorConsentRecord | null, absent: false });
          })
        );
      })
    );
  }

  private fetchVendorConsentFromUrl(url: string, headers: HttpHeaders) {
    type VendorConsentRecord = {
      accepted: boolean;
      consentVersion: string;
      consentText: string;
      acceptedAt: string;
    };

    return this.http
      .get<VendorConsentRecord>(url, { headers })
      .pipe(map((record) => ({ record, absent: false })));
  }

  /**
   * Lazy per-file binary download.
   * Backend: GET /api/companydocument/download/{companyId}/{fileType}
   * (Swap path here if the API moves to /company-documents/.../download/...)
   */
  downloadCompanyDocument(companyId: number, fileType: string) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        return this.http.get(`${this.baseUrl}/companydocument/download/${companyId}/${encodeURIComponent(fileType)}`, {
          headers,
          responseType: 'blob',
          observe: 'response',
        }) as Observable<HttpResponse<Blob>>;
      }),
      catchError((error) => throwError(() => error))
    );
  }

  //200
  getCompanyHSE(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        return this.http.get<any[]>(`${this.baseUrl}/companyHSE?companyId=${companyId}`, { headers }).pipe(
          // tap(response => console.log('HSE Response:', response))
        );
      }),
      catchError((error) => {
        console.error('Failed to fetch getCompanyHSE:', error);
        return throwError(() => error);
      })
    );
  }

  //200
  getCompanySignatories(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        return this.http.get<any[]>(`${this.baseUrl}/companysignatory?companyId=${companyId}`, { headers }).pipe(
          // tap(response => console.log('Signatories Response:', response))
        );
      }),
      catchError((error) => {
        console.error('Failed to fetch getCompanySignatories:', error);
        return throwError(() => error);
      })
    );
  }

  //200
  getCompanySubcontracts(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        return this.http.get<any[]>(`${this.baseUrl}/companysubcontract?companyId=${companyId}`, { headers }).pipe(
          // tap(response => console.log('Subcontracts Response:', response))
        );
      }),
      catchError((error) => {
        console.error('Failed to fetch getCompanySubcontracts:', error);
        return throwError(() => error);
      })
    );
  }

  //200
  getCompanySubsidiaries(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap(() => {
        const headers = this.getAuthHeaders();
        //filtering in backend
        return this.http.get<any[]>(`${this.baseUrl}/companysubsidary?companyId=${companyId}`, { headers }).pipe(
          // tap(response => console.log('Subsidaries Response:', response))
        );
      }),
      catchError((error) => {
        console.error('Failed to fetch getCompanySubsidiaries:', error);
        return throwError(() => error);
      })
    );
  }



  
//This returns the Faq items..


  getSubmissionsSettings(): Observable<{ submissionsRestricted: boolean }> {
    return this.http.get<{ submissionsRestricted: boolean }>(
      `${this.baseUrl}/settings/submissions`
    );
  }

  updateSubmissionsSettings(submissionsRestricted: boolean): Observable<{ submissionsRestricted: boolean }> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        });
        return this.http.put<{ submissionsRestricted: boolean }>(
          `${this.baseUrl}/settings/submissions`,
          { submissionsRestricted },
          { headers }
        );
      }),
      catchError((error) => {
        console.error('Failed to update submissions settings:', error);
        return throwError(() => error);
      })
    );
  }

  getFaqItems(): Observable<any> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        // Add query parameter for intPageFK = 4
        const url = `${this.Faq}`;
        return this.http.get<any>(url, { headers });
      }),
      catchError((error) => {
        console.error('Failed to fetch FAQ items:', error);
        return throwError(() => error);
      })
    );
  }
  
  
  getpage(): Observable<any> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.get<any>(this.pageUrl, { headers });
      }),
      catchError((error) => {
        console.error('Failed to fetch pages:', error);
        return throwError(() => error);
      })
    );
  }


  getUsers(): Observable<any> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        console.log('Successfully retrieved users')
        return this.http.get<any[]>(`${this.baseUrl}/user`, { headers });
      }),
      catchError((error) => {
        console.error('Failed to fetch users:', error);
        return throwError(() => error);
      })
    );
  }

  
//FAQ
 setFaqItems(intFaqPK: number, recordData: any): Observable<any> { //for editing
    return this.authService.handleTokenExpiration().pipe(
    switchMap((accessToken: string) => {
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${accessToken}`,
      });
      const url = `${this.Faq}/${intFaqPK}`;
      return this.http.put(url, recordData, { headers });}),
      catchError((error) => {
        console.error('[setFaqItems] Failed:', error);
        return throwError(() => error);
      })
    );
  }  


  addFaqItems(recordData: any): Observable<any> {
    return this.authService.handleTokenExpiration().pipe(
    switchMap((accessToken: string) => {
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${accessToken}`,
      });
      const url =this.Faq;
      return this.http.post(url, recordData, { headers });}),
      catchError((error) => {
        console.error('[addFaqItems] Failed:', error);
        return throwError(() => error);
      })
    );
  }  

  EmailPasswordReset(email: string): Observable<any> {
    const url = this.baseUrl + "/password/reset";
    const payload = {email};

    // console.log("Payload: ", payload);
    return this.http.post(url, payload).pipe(
      catchError((error) => {
        console.error('Failed to send password reset email:', error);
        return throwError(() => error);
      })
    );
  };



  ChangePassword(payload: any): Observable<any> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        });
  
        const url = this.baseUrl + "/password/change";
  
        return this.http.post(url, payload, { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed to change password:', error);
        return throwError(() => error);
      })
    );
  }
  
 
  
  

  
   deleteFAQ(intFaqPK: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.delete(`${this.baseUrl}/faq/${intFaqPK}`, { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
    
  }


  updateAccountStatus(userId: number, status: string) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.patch(
          `${this.baseUrl}/user/${userId}/status`,
          { strAccountStatus: status },
          { headers: headers }
        );
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }

  updateFlagged(userId: number, blnFlagged: boolean) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.patch(
          `${this.baseUrl}/user/flag/${userId}`,
          blnFlagged,
          { headers: headers }
        );
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }

  //COMPANY NOTES
  getCompanyNotes(companyId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.get<any[]>(`${this.baseUrl}/companynote/${companyId}/notes`,  { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }
  
  addCompanyNote(companyId: number, content: string, adminId: number, vendorId?: number | null) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        const body: { content: string; adminId: number; vendorId?: number } = { content, adminId };
        if (vendorId != null) {
          body.vendorId = vendorId;
        }
        return this.http.post(`${this.baseUrl}/companynote/${companyId}/notes`, body, { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }

  /** Vendor-only: chronological procurement notes for the logged-in vendor. */
  getMyVendorMessages() {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.get<
          { id: number; noteContent: string; createdAt: string; senderName: string | null }[]
        >(`${this.baseUrl}/user/me/procurement-messages`, { headers }).pipe(
          map((data) => (Array.isArray(data) ? data : []))
        );
      }),
      catchError((error) => {
        console.error('Failed to load vendor messages', error);
        return throwError(() => error);
      })
    );
  }

  deleteCompanyNote(noteId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.delete(`${this.baseUrl}/companynote/notes/${noteId}`, { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
    
  }




  //AUDIT LOG
  getAuditLog() {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.get<any[]>(`${this.baseUrl}/audit/all`,  { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }
  
  addAuditLog(activity: string, adminId: number) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.post(`${this.baseUrl}/audit/new`, {
          activity: activity,
          adminId: String(adminId)
        }, { headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }

  EmailCsv(payload: any): Observable<any> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        });
        const url = this.baseUrl + "/email/send-csv";
        return this.http.post(url, payload, { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed to create Csv', error);
        return throwError(() => error);
      })
    );
  }
CreateUser(payload: any): Observable<any> {
  return this.authService.handleTokenExpiration().pipe(
    switchMap(() => {
      // After token refresh or validation, grab the token from localStorage
      const token = localStorage.getItem('jwt token');
      if (!token) {
        return throwError(() => new Error('No JWT token found in localStorage'));
      }

      const headers = new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });

      return this.http.post(this.baseUrl, payload, { headers });
    }),
    catchError((error) => {
      console.error('CreateUser error:', error);
      return throwError(() => error);
    })
  );
}


  
}